/**
 * bb-location-banner.js
 * ───────────────────────────────────────────────────────────────────────────
 * Location banner / confirm-ZIP nudge (spec §4 + §5).
 *
 * Reacts to `bb:location-resolved` and shows one of these states (keyed off the
 * `served` determination, NOT merely "region is null"):
 *   - SERVED + guessed (ipapi)            → SOFT nudge with the state name
 *   - SERVED + confirmed (manual ZIP)     → hidden (never nudge again)
 *   - KNOWN-UNSERVED (served === false)   → firm RED "we don't serve…" banner
 *   - UNKNOWN (served == null: transient geo failure / no detection) → SOFT
 *     fallback nudge, NOT the firm banner — a one-off rate-limit must never tell
 *     a served visitor we don't serve their area.
 *
 * SECURITY (§0.5): all dynamic text (incl. the state name) is written via
 * textContent — never innerHTML — so tampered data can't inject markup. Copy is
 * read from the banner's data-* attributes (authored in Liquid, localizable).
 *
 * The CTA opens the manual ZIP modal (Phase 5) by dispatching BB_OPEN_ZIP_MODAL;
 * until that exists the click is a harmless no-op.
 */

import { getStateName } from '@theme/bb-region-config';
import { BB_LOCATION_EVENT, getLastResolved } from '@theme/bb-geo-detect';

/** Dispatched on `document` when the visitor asks to enter/confirm their ZIP. */
export const BB_OPEN_ZIP_MODAL = 'bb:open-zip-modal';

const BANNER_ID = 'bb-location-banner';

/**
 * Re-sum #header-group's height into --header-group-height. The banner now lives
 * inside #header-group (see theme.liquid [R-04]) but the theme only computes this
 * var on load / resize — not when the banner reveals or hides. Without this, the
 * fixed BROWSE mega panel (top: var(--header-group-height)) would overlap the
 * banner. Mirrors the inline calc in theme.liquid. Best-effort: no-op if the
 * header group isn't present (e.g. password page).
 */
function syncHeaderGroupHeight() {
  if (typeof document === 'undefined') return;
  const group = document.getElementById('header-group');
  if (!group) return;
  const headerComponent = document.querySelector('header-component');
  let total = 0;
  for (const el of group.children) {
    if (el === headerComponent || !(el instanceof HTMLElement)) continue;
    total += el.offsetHeight;
  }
  document.body.style.setProperty('--header-group-height', `${Math.round(total)}px`);
}

function getRefs() {
  const banner = document.getElementById(BANNER_ID);
  if (!banner) return null;
  return {
    banner,
    message: banner.querySelector('#bb-location-banner-message'),
    cta: banner.querySelector('#bb-location-banner-cta'),
  };
}

function hide(refs) {
  refs.banner.setAttribute('data-bb-state', 'hidden');
  refs.banner.setAttribute('hidden', '');
  // Clear the stale copy AND release the pre-paint space reservation. Without the
  // release, html[data-bb-banner="reserve"] .bb-location-banner (set once by
  // bb-flash-guard, higher specificity than [hidden]) keeps the now-stale "out of
  // area" bar occupying its slot until the next navigation re-runs flash-guard —
  // so a visitor who fixes their ZIP to a served one still sees the old banner.
  // hide() only runs on a resolved SERVED + confirmed location, i.e. a user action,
  // so this collapse is exempt from CLS (layout shift within 500ms of input).
  if (refs.message) refs.message.textContent = '';
  if (refs.cta) refs.cta.textContent = '';
  if (typeof document !== 'undefined' && document.documentElement) {
    document.documentElement.setAttribute('data-bb-banner', 'none');
  }
  syncHeaderGroupHeight();
}

/**
 * Reveal the banner, then set the message + CTA text on the next frame so the
 * aria-live region is already rendered when the text is inserted — toggling
 * visibility and content in the same tick is unreliably announced.
 */
function announce(refs, state, message, ctaLabel) {
  refs.banner.setAttribute('data-bb-state', state);
  refs.banner.removeAttribute('hidden');
  const apply = () => {
    refs.message.textContent = message;
    refs.cta.textContent = ctaLabel;
    // Banner is now visible with its final copy → fold its height into the header
    // group var so the BROWSE mega panel clears it.
    syncHeaderGroupHeight();
  };
  if (typeof requestAnimationFrame === 'function') requestAnimationFrame(apply);
  else apply();
}

function showSoft(refs, stateName) {
  const { softTemplate = '', softFallback = '', softCta = '' } = refs.banner.dataset;

  // `[region]` placeholder rather than the previous prefix/suffix pair. The copy is
  // merchant-editable now (Bower & Branch → Location banner), and prefix/suffix
  // relied on a trailing space in "Showing plants for " surviving a round trip
  // through a theme-editor text field — lose it and the state name runs straight
  // into the preceding word. split/join replaces every occurrence and, unlike
  // String.replace with a string pattern, has no special-character behaviour to
  // trip over. Still rendered via textContent, so the merchant string is inert.
  const message = stateName && softTemplate ? softTemplate.split('[region]').join(stateName) : softFallback;
  announce(refs, 'soft', message, softCta);
}

function showUnmapped(refs) {
  const { unmappedMessage = '', unmappedCta = '' } = refs.banner.dataset;
  announce(refs, 'unmapped', unmappedMessage, unmappedCta);
}

function render(detail) {
  const served = detail && typeof detail.served === 'boolean' ? detail.served : null;

  // Reflect status on the nav region pill's dot (moss = located/served, accent =
  // not). Unknown (served null) shows the accent dot — accurate ("not set yet") —
  // while the banner stays SOFT, so only a positively-unserved visitor sees red.
  if (typeof document !== 'undefined') {
    for (const trigger of document.querySelectorAll('.bb-zip-trigger')) {
      trigger.setAttribute('data-bb-region', served === true ? 'mapped' : 'unmapped');
    }
  }

  const refs = getRefs();
  if (!refs || !refs.message || !refs.cta) return;

  const confirmed = Boolean(detail && detail.zip_confidence === 'confirmed');

  if (served === false) {
    showUnmapped(refs); // KNOWN-unserved (D–I / AK-HI / non-US) → firm banner
  } else if (served === true && confirmed) {
    hide(refs); // confirmed in a served region → no nudge
  } else {
    // Served-but-guessed → "Showing plants for <State>". Unknown → soft fallback.
    showSoft(refs, getStateName(detail && detail.state));
  }
}

function boot() {
  if (typeof document === 'undefined') return;
  // Don't nudge merchants editing in the theme editor (no real visitor).
  if (window.Shopify && window.Shopify.designMode) return;

  // The banner only exists on product and collection pages (see
  // bb-location-banner.liquid), but render() ALSO drives the nav ZIP pill's dot,
  // which is in the header on every page. Returning early when there is no banner
  // would leave that dot stuck in its served-looking default for an unserved
  // visitor everywhere else — the pill restyles only on data-bb-region="unmapped",
  // so "never set" and "served" look identical. Bind the listeners regardless and
  // let render()'s own refs guard skip just the banner half.
  const refs = getRefs();

  if (refs && refs.cta) {
    refs.cta.addEventListener('click', () => {
      document.dispatchEvent(new CustomEvent(BB_OPEN_ZIP_MODAL));
    });
  }

  document.addEventListener(BB_LOCATION_EVENT, (event) => render(event.detail));

  const last = getLastResolved();
  if (last !== undefined) render(last);
}

boot();
